'use client';

import { LandingHeader } from '@/components/landing/header';
import { LandingBreadcrumbs } from '@/components/landing/breadcrumbs';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

type Priority = 'low' | 'medium' | 'high' | null;

export default function SupportTicketsPage() {
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [priority, setPriority] = useState<Priority>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newTicketId = `#RUD-${Math.floor(Math.random() * 10000)}`;
    setTicketId(newTicketId);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <>
        <main>
          <section className="bg-background py-12 md:py-16 px-4">
            <div className="max-w-2xl mx-auto">
              <LandingBreadcrumbs
                items={[
                  { label: 'Help', href: '/help' },
                  { label: 'Support Tickets' },
                ]}
              />

              <div className="border border-border rounded-lg p-12 text-center bg-white">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <h1 className="text-3xl font-bold mb-2">
                  Ticket Submitted Successfully
                </h1>
                <p className="text-muted-foreground mb-6">
                  ID:{' '}
                  <span className="font-mono text-foreground">{ticketId}</span>
                </p>
                <p className="text-muted-foreground mb-8">
                  Our engineering team has received your report.
                  <br />
                  We typically respond within 24 hours.
                </p>
                <Button onClick={() => (window.location.href = '/')}>
                  Return to Homepage
                </Button>
              </div>
            </div>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
      <LandingHeader />
      <main>
        <section className="bg-background py-12 md:py-16 px-4">
          <div className="max-w-2xl mx-auto">
            <LandingBreadcrumbs
              items={[
                { label: 'Help', href: '/help' },
                { label: 'Support Tickets' },
              ]}
            />

            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Support <span className="text-primary">Tickets</span>
              </h1>
              <p className="text-muted-foreground">
                Tell us what you need help with, and our team will get right on
                it.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="border border-border rounded-lg p-8 bg-white"
            >
              {/* Title Field */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  Title
                </label>
                <input
                  type="text"
                  placeholder="PDF fails to compile"
                  required
                  className="w-full border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Category/Type */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  Category/Type
                </label>
                <select
                  required
                  className="w-full border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select Category/Type</option>
                  <option>Technical Issue</option>
                  <option>Account Issue</option>
                  <option>Billing Question</option>
                  <option>Feature Request</option>
                  <option>Other</option>
                </select>
              </div>

              {/* Priority */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-3">
                  Priority
                </label>
                <div className="flex gap-4">
                  {(['low', 'medium', 'high'] as const).map((p) => (
                    <label
                      key={p}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="priority"
                        value={p}
                        checked={priority === p}
                        onChange={(e) =>
                          setPriority(e.target.value as Priority)
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-sm capitalize">{p}</span>
                      <span
                        className={`w-2 h-2 rounded-full ${
                          p === 'low'
                            ? 'bg-green-500'
                            : p === 'medium'
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                        }`}
                      />
                    </label>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  Detailed Description
                </label>
                <textarea
                  placeholder="Enter a detailed description..."
                  required
                  rows={6}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  Attachment
                </label>
                <div className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center bg-blue-50">
                  <svg
                    className="w-12 h-12 text-primary mx-auto mb-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                  <p className="text-sm text-muted-foreground">
                    Supported formats: jpg, png, zip, .tex (Max 20MB)
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Drag and drop or{' '}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                    >
                      Browse files
                    </button>
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full">
                Create Ticket
              </Button>
            </form>
          </div>
        </section>
      </main>
    </>
  );
}
